import { Button, ButtonVariants } from '@triumph/design-system';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { useResolveConditionMutation } from '@/components/shared/api/mutations/conditions';
import { Dialog } from '@/components/shared/components/Dialog';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import {
    MentionTextarea,
    useMentionSuggestions,
} from '@/components/shared/components/MentionTextarea';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    ErrorText,
    FormActions,
    FormLabel,
    HelpText,
    ModalContainer,
    ModalField,
    ModalSubtitle,
    ProblematicButton,
    ReadonlyConditionTitle,
    RequiredAsterisk,
    ResolveButton,
} from './ApplicantConditions.StyledComponents';
import {
    Condition,
    ConditionResolutionMethod,
    ConditionResolutionStatus,
} from './ApplicantConditions.types';

interface Props {
    applicantId: string;
    condition: Condition | null;
    visible: boolean;
    onClose: () => void;
    onConflict?: () => void;
    /** Seeds the resolution note (e.g. an in-progress draft or the Auto Resolve template). */
    initialNote?: string;
    /** How the resolution is recorded; defaults to Manual. */
    resolutionMethod?: ConditionResolutionMethod;
    /** Called after a successful Problematic resolution, to offer spawning a follow-up. */
    onProblematic?: (condition: Condition) => void;
    /** Called after any successful resolution, before onProblematic. */
    onResolved?: (condition: Condition) => void;
}

export const ResolveConditionDialog = ({
    applicantId,
    condition,
    visible,
    onClose,
    onConflict,
    initialNote = '',
    resolutionMethod = ConditionResolutionMethod.Manual,
    onProblematic,
    onResolved,
}: Props) => {
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mentionSuggestions = useMentionSuggestions(applicantId);

    const { isConditionResolutionStatusEnabled, isConditionTextFormattingEnabled } =
        useFeatureFlags();

    const { mutate: resolveCondition, isPending } = useResolveConditionMutation();

    useEffect(() => {
        if (visible) {
            setNote(initialNote);
            setError(null);
        }
    }, [visible, condition?.id, initialNote]);

    const handleClose = () => {
        onClose();
    };

    const handleResolve = (resolutionStatus?: ConditionResolutionStatus) => {
        if (!condition) return;
        const trimmed = note.trim();
        if (!trimmed) {
            setError('A resolution note is required.');
            return;
        }
        resolveCondition(
            {
                applicantId,
                conditionId: condition.id,
                note: trimmed,
                resolutionMethod,
                resolutionStatus,
            },
            {
                onSuccess: () => {
                    handleClose();
                    onResolved?.(condition);
                    if (resolutionStatus === ConditionResolutionStatus.Problematic) {
                        onProblematic?.(condition);
                    }
                },
                onError: (err) => {
                    if (isAxiosError(err) && err.response?.status === 409) {
                        onConflict?.();
                        handleClose();
                    } else {
                        setError('Failed to resolve condition. Please try again.');
                    }
                },
            },
        );
    };

    return (
        <Dialog
            id="resolve-condition-modal"
            header="Resolve Task/Condition"
            visible={visible}
            onHide={handleClose}
            dismissableMask={false}
            data-testid="resolve-condition-dialog"
        >
            <ModalContainer>
                <ReadonlyConditionTitle
                    $markdown={isConditionTextFormattingEnabled}
                    data-testid="resolve-condition-description"
                >
                    {isConditionTextFormattingEnabled ? (
                        <FormattedText>{condition?.description ?? ''}</FormattedText>
                    ) : (
                        <Linkify>{condition?.description ?? ''}</Linkify>
                    )}
                </ReadonlyConditionTitle>

                <ModalField>
                    <FormLabel htmlFor="resolve-condition-note">
                        Resolution note<RequiredAsterisk>*</RequiredAsterisk>
                    </FormLabel>
                    <MentionTextarea
                        id="resolve-condition-note"
                        data-testid="resolve-condition-note"
                        placeholder="Describe what you verified, or what still needs to happen…"
                        value={note}
                        onChange={(val) => {
                            setNote(val);
                            if (error) setError(null);
                        }}
                        hasError={Boolean(error)}
                        spellCheck
                        suggestions={mentionSuggestions}
                    />
                    {error ? (
                        <ErrorText>{error}</ErrorText>
                    ) : (
                        <HelpText>
                            A resolution note is required — Remember to describe how this was
                            verified.
                        </HelpText>
                    )}
                </ModalField>

                <FormActions style={{ justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <Button
                        variant={ButtonVariants.secondary}
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    {isConditionResolutionStatusEnabled ? (
                        <ProblematicButton
                            type="button"
                            onClick={() => handleResolve(ConditionResolutionStatus.Problematic)}
                            disabled={isPending || !condition}
                            data-testid="resolve-condition-problematic"
                        >
                            ⚠ Flag as Problematic
                        </ProblematicButton>
                    ) : null}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <ResolveButton
                            type="button"
                            onClick={() =>
                                handleResolve(
                                    isConditionResolutionStatusEnabled
                                        ? ConditionResolutionStatus.Approved
                                        : undefined,
                                )
                            }
                            disabled={isPending || !condition}
                            data-testid="resolve-condition-confirm"
                        >
                            ✓ Mark Task/Condition Resolved
                        </ResolveButton>
                        <ModalSubtitle style={{ margin: 0 }}>
                            <strong style={{ textDecoration: 'underline' }}>
                                This cannot be undone.
                            </strong>
                        </ModalSubtitle>
                    </div>
                </FormActions>
            </ModalContainer>
        </Dialog>
    );
};
